import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../Controllers/Store";

import { Toast } from "primereact/toast";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Nullable } from "primereact/ts-helpers";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";

import ErrorPage from "../../../../../../Components/ErrorPage";
import DatePicker from "../../../../../../Components/DatePicker";
import TextInput from "../../../../../../Components/TextInput";
import PageLoader from "../../../../../../Components/PageLoader";
import DetailLoader from "../DetailLoader";
import GoBackButton from "../../../../../../Components/GoBackButton";
import { hasPermission } from "../../../../../../Controllers/Selectors/permissionsSelector";
import { checkModelSelected, checkText } from "../../../../../../Utils/Validator";
import { checkIsArchitect, checkIsBuildingInspector, checkIsClient } from "../../../../../../Utils/LoginUserRoleName";

import { formatDate, parseStringToDate } from "../../../../../../Utils/getDate";
import { DutyHolderDetail } from "../../../../../../Controllers/Types/DutyHolderDetails/DutyHolderDetailsTypes";
import { fetchDutyHolderData, fetchDutyHolderTypes } from "../../../../../../Controllers/Thunks/DutyHolderDetailThunks";
import NoDataCreated from "../../../../../../Components/NoDataCreated";
import { DutyHolderData, DutyHolderType } from "../../../../../../Controllers/Types/DutyHolderDetails/New/DutyHolderDetailsTypes";
import { Dropdown, DropdownChangeEvent, DropdownProps } from "primereact/dropdown";
import DataListModalInput from "../../../../../../Components/DataListModal/DataListModalInput";
import TabFilter from "../../../../../../Components/TabFilter";
import { ProjectBasicContact } from "../../../../../../Controllers/Types/Project/ProjectInterface";
import { confirmDialog } from "primereact/confirmdialog";
import { Message } from "primereact/message";
import ViewDataGroup from "../../../../../../Components/DataViewModal/ViewDataGroup";
import { addDutyHolderTypeAPI, deleteDutyHolderOtherDataAPI, signComplianceDeclarationAPI, updateDutyHolderClientDataAPI, updateDutyHolderOtherDataAPI } from "../../../../../../Api/DutyHolderDetailApi";

const contactTabs = ["All", "Employee", "Professional"] as const;
type ContactTab = typeof contactTabs[number];

const DutyHolderDetails: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);

    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);
    const contactLists = projectData.projectAllContact as ProjectBasicContact[];
    const isShared: boolean = projectData?.sharedBy !== null;
    const canEdit: boolean = Boolean(projectData?.can_edit);

    const [showDutyHolderRequestModal, setShowDutyHolderRequestModal] = useState<boolean>(false);

    // Form fields
    const [selectedDutyHolderType, setSelectedDutyHolderType] = useState<DutyHolderType | null>(null);
    const [selectedContact, setSelectedContact] = useState<ProjectBasicContact | null>(null);

    // Form errors
    const [dutyHolderTypeError, setDutyHolderTypeError] = useState<string>('');
    const [contactError, setContactError] = useState<string>('');

    const [showContactListModal, setShowContactListModal] = useState<boolean>(false);
    const [contactSearchKey, setContactSearchKey] = useState<string>('');
    const [contactTypeTab, setContactTypeTab] = useState<ContactTab>("All");

    // Declaration
    const [selectedReviewDeclaration, setSelectedReviewDeclaration] = useState<DutyHolderData | null>(null);
    const [showDeclarationModal, setShowDeclarationModal] = useState<boolean>(false);

    // Declaration form fields
    const [name, setName] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [telephone, setTelephone] = useState<string>('');
    const [date, setDate] = useState<Nullable<Date>>(null);

    // Declaration form errors
    const [nameError, setNameError] = useState<string>('');
    const [addressError, setAddressError] = useState<string>('');
    const [telephoneError, setTelephoneError] = useState<string>('');
    const [dateError, setDateError] = useState<string>('');

    const [isAcceptDeclaration, setIsAcceptDeclaration] = useState<boolean>(false);

    const {
        loadingDutyHolderTypes, errorDutyHolderTypes, dutyHolderTypes,
        loadingDutyHolderData, errorDutyHolderData, dutyHolderData
    } = useAppSelector((state) => state?.dutyHolderDetail);

    const dutyHolderTypeOptions = dutyHolderTypes?.map((dutyHolderType: DutyHolderType) => ({
        ...dutyHolderType,
        disabled: dutyHolderType.is_added === true,
    })) ?? [];

    const filteredContactsData = useMemo(() => {
        const searchKey = contactSearchKey.toLowerCase().trim();

        return contactLists.filter((contact) => {
            const isAllowedContactType =
                contact.main_type === "Employee" || contact.main_type === "Professional";

            if (!isAllowedContactType) {
                return false;
            }

            const matchesTypeTab =
                contactTypeTab === "All" || contact.main_type === contactTypeTab;

            const firstName = contact.first_name?.toLowerCase() ?? "";
            const lastName = contact.last_name?.toLowerCase() ?? "";
            const fullName = `${firstName} ${lastName}`.trim();

            const matchesSearch =
                !searchKey ||
                firstName.includes(searchKey) ||
                lastName.includes(searchKey) ||
                fullName.includes(searchKey);

            return matchesTypeTab && matchesSearch;
        });
    }, [contactLists, contactSearchKey, contactTypeTab]);

    // Check role
    const [isClientUser, setIsClientUser] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [clientResult] = await Promise.all([
                checkIsClient(),
            ]);

            setIsClientUser(clientResult);
        };

        checkRole();
    }, []);

    // Permission handler
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionAddClient = useCanEdit("Dutyholder", "add_client");
    const hasPermissionEditClient = useCanEdit("Dutyholder", "edit_client");
    const hasPermissionAddDesigner = useCanEdit("Dutyholder", "add_designer");
    const hasPermissionEditDesigner = useCanEdit("Dutyholder", "edit_designer");
    const hasPermissionAddContractor = useCanEdit("Dutyholder", "add_contractor");
    const hasPermissionEditContractor = useCanEdit("Dutyholder", "edit_contractor");


    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
        };

        checkRole();
    }, []);
    /*  */

    const checkAccessPermission = useCallback(
        (permission: boolean): boolean => {
            return isShared ? canEdit : permission;
        },
        [isShared, canEdit]
    );

    const handleGetDutyHolderTypes = useCallback(async () => {
        if (!folderId) return;

        try {
            await dispatch(fetchDutyHolderTypes(folderId)).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch duty holder types.",
                life: 3000,
            });
        } finally {
            setInitialDataLoading(false);
        }
    }, [dispatch, folderId]);

    const handleGetDutyHolderData = useCallback(async () => {
        if (!folderId) return;

        try {
            await dispatch(fetchDutyHolderData(folderId)).unwrap();
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch duty holder data.",
                life: 3000,
            });
        } finally {
            setInitialDataLoading(false);
        }
    }, [dispatch, folderId]);

    useEffect(() => {
        handleGetDutyHolderTypes();
        handleGetDutyHolderData();
    }, [handleGetDutyHolderData, handleGetDutyHolderTypes]);

    const handleAddDutyHolderRequest = () => {
        setShowDutyHolderRequestModal(true);
    }

    const handleCloseDutyHolderRequestModal = () => {
        setShowDutyHolderRequestModal(false);
    }



    function checkRequestCredentials(): boolean {
        const contact = checkModelSelected(selectedContact !== null, true, setContactError);
        const type = checkModelSelected(selectedDutyHolderType !== null, true, setDutyHolderTypeError);

        return contact && type;
    }

    function showAPIErrorCustomRequestData(keyName: string, error: string): void {
        if (keyName === "id") {
            setContactError(error);
        } else if (keyName === "dutyholder_type_id") {
            setDutyHolderTypeError(error);
        }
        else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error request .",
                    life: 3000,
                });
            }
        }
    }

    // request database function.




    const handleCreateDutyHolderRequest = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkRequestCredentials()) {
            setLoading(true);
            const data = await addDutyHolderTypeAPI(folderId, selectedDutyHolderType?.id!, selectedContact?.id!, selectedContact?.by!);

            setLoading(false);
            handleCloseDutyHolderRequestModal();

            if (data.statusCode === 200) {
                // call company dispach event
                handleGetDutyHolderData();
                handleGetDutyHolderTypes();
                setSelectedContact(null);
                setSelectedDutyHolderType(null);


                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "Requested successfully.",
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value]) => (
                    showAPIErrorCustomRequestData(key, value[0])
                ))
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error Requesting duty holder details.",
                        life: 3000,
                    });
                }
            }
        }
    }



    const dutyHolderRequestModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Add Duty Holder Request</h5>
            <button
                type="button"
                className="close_modal_btn p-ripple"
                onClick={handleCloseDutyHolderRequestModal}
            >
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const dutyHolderRequestModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary d-none d-sm-flex"
                onClick={handleCloseDutyHolderRequestModal}
            />

            <Button
                label={loading ? "Processing" : "Submit"}
                onClick={handleCreateDutyHolderRequest}
                className="custom_btn primary"
                loading={loading}
            />
        </div>
    );

    const selectedDutyHolderTypeTemplate = (option: DutyHolderType, props: DropdownProps) => {
        if (option) {
            return (
                <span>
                    {option?.dutyholder_type}
                </span>
            );
        }

        return <span>{props.placeholder}</span>;
    };

    const dutyHolderTypeOptionTemplate = (option: DutyHolderType & { disabled?: boolean }) => {
        return (
            <div
                className={`address_option_container d-flex justify-content-between align-items-center bg-purple 
                        ${option?.disabled ? "disabled_option" : ""}
                    `}
            >
                <span>
                    {option?.dutyholder_type}

                    {option?.disabled && (
                        <small className="ms-2 text-danger">
                            (Already requested)
                        </small>
                    )}
                </span>
            </div>
        );
    };

    const handleRemoveDutyHolder = (dutyHolder: DutyHolderData | null) => {
        if (!dutyHolder) return;

        const dutyHolderId = dutyHolder.id;

        confirmDialog({
            message: (
                <div className="text-center d-flex justify-content-center flex-column">
                    <span>Are you sure you want to remove this <b>duty holder</b>?</span>
                    <span>This action <b>cannot be undone</b>.</span>
                </div>
            ),
            header: "Confirmation",
            icon: "bi bi-info-circle",
            defaultFocus: "accept",
            acceptClassName: "p-button-danger",
            accept: () => removeDutyHolder(dutyHolderId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }


    // delet api calling from data base function
    const removeDutyHolder = async (dutyHolderId: number) => {
        const result = await deleteDutyHolderOtherDataAPI(dutyHolderId);

        if (result.statusCode === 200) {
            handleGetDutyHolderData();
            handleGetDutyHolderTypes();

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Record deleted successfully.",
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error deleting the record.",
                    life: 3000,
                });
            }
        }
    };




    // Contact list
    const handleCloseContactModal = () => {
        setShowContactListModal(false);
    }

    const handleAddContct = () => {
        setShowContactListModal(true);
    };

    const handleSelectContact = (contact: ProjectBasicContact) => {
        if (!contact) return;

        setSelectedContact(contact);

        setTimeout(() => {
            setShowContactListModal(false);
        }, 300);
    }

    const contactModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Select contact
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseContactModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    // Declaration
    const handleShowDeclarationModal = (dutyHolder: DutyHolderData | null) => {
        if (!dutyHolder) return;
        setSelectedReviewDeclaration(dutyHolder);
        setShowDeclarationModal(true);

        const isClient = dutyHolder?.dutyholder_type?.is_client === 1;

        const dutyHolderName = isClient ? (dutyHolder?.client_name ?? '') : (dutyHolder?.name ?? '');

        // Set details
        setName(dutyHolderName ?? '');
        setTelephone(dutyHolder?.telephone ?? '');
        setAddress(dutyHolder?.address ?? '');

        setShowDeclarationModal(true);
    }

    const handleResetDeclarationFields = () => {
        setName('');
        setTelephone('');
        setAddress('');
        setDate(null);

        setNameError('');
        setTelephoneError('');
        setAddressError('');
        setDateError('');

        setSelectedReviewDeclaration(null);
    }

    const handleCloseDeclarationModal = () => {
        setShowDeclarationModal(false);
        handleResetDeclarationFields();
    }


    // DINESH Need to check validation .
    // accept API function calling
    const handleAcceptDeclaration = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedReviewDeclaration) return;

        setLoading(true);

        const isClient = selectedReviewDeclaration?.dutyholder_type?.dutyholder_type === "Client";

        if (isClient) {
            // client api service call.
            const data = await updateDutyHolderClientDataAPI(
                selectedReviewDeclaration?.id,
                name,
                address,
                selectedReviewDeclaration?.email,
                telephone,
                formatDate(date!)

            )

            if (data.statusCode === 200) {
                setLoading(false);
                const result = await signComplianceDeclarationAPI(
                    selectedReviewDeclaration?.id,
                    declareThat
                )

                if (result.statusCode === 200) {
                    setLoading(false);
                    handleGetDutyHolderData();
                    handleCloseDeclarationModal();
                    if (toast.current) {
                        toast.current.show({
                            severity: "success",
                            summary: "Success",
                            detail: "Accepted successfully.",
                            life: 3000,
                        });
                    }
                } else {
                    setLoading(false);
                    handleCloseDeclarationModal();
                    if (toast.current) {
                        toast.current.show({
                            severity: "error",
                            summary: "Failed",
                            detail: "There was an error accepting the record.",
                            life: 3000,
                        });
                    }
                }


            } else {
                setLoading(false);
                handleCloseDeclarationModal();
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error accepting the record.",
                        life: 3000,
                    });
                }
            }

        } else {
            // other api service call.
            const data = await updateDutyHolderOtherDataAPI(
                selectedReviewDeclaration?.id,
                name,
                address,
                selectedReviewDeclaration?.email,
                telephone,
                formatDate(date!)

            )

            if (data.statusCode === 200) {
                setLoading(false);
                const result = await signComplianceDeclarationAPI(
                    selectedReviewDeclaration?.id,
                    declareThat
                )

                if (result.statusCode === 200) {
                    setLoading(false);
                    handleGetDutyHolderData();
                    handleCloseDeclarationModal();
                    if (toast.current) {
                        toast.current.show({
                            severity: "success",
                            summary: "Success",
                            detail: "Accepted successfully.",
                            life: 3000,
                        });
                    }
                } else {
                    setLoading(false);
                    handleCloseDeclarationModal();
                    if (toast.current) {
                        toast.current.show({
                            severity: "error",
                            summary: "Failed",
                            detail: "There was an error accepting the record.",
                            life: 3000,
                        });

                    }
                }


            } else {
                setLoading(false);
                handleCloseDeclarationModal();
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "There was an error accepting the record.",
                        life: 3000,
                    });
                }
            }

        }





    }

    const declarationModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Review declaration
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseDeclarationModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const declarationModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary d-none d-sm-flex"
                onClick={handleCloseDeclarationModal}
            />

            <Button
                label={loading ? "Processing" : "Accept"}
                onClick={handleAcceptDeclaration}
                className="custom_btn primary"
                disabled={!isAcceptDeclaration || date === null}
                loading={loading}
            />
        </div>
    );

    const declareThat = useMemo(() => {
        if (selectedReviewDeclaration?.dutyholder_type?.dutyholder_type === 'Client') {
            return 'The building work is complete, and, To the best of my knowledge, the building work complies with the Building Regulations.';
        } else {
            return 'I have fulfilled my duties under Part 2A of the Building Regulations 2010.';
        }
    }, [selectedReviewDeclaration]);

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingDutyHolderData && loadingDutyHolderTypes && initialDataLoading ? (
                    <PageLoader />
                ) : errorDutyHolderTypes || errorDutyHolderData ? (
                    <ErrorPage errorMessage={errorDutyHolderTypes ?? errorDutyHolderData ?? ''} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3 justify-content-end">
                            <div className="page_action_area">
                                {(isArchitect) && (<button
                                    className="add_data_link_btn p-ripple"
                                    onClick={handleAddDutyHolderRequest}
                                >
                                    <i className="bi bi-plus-circle"></i>
                                    <span>Add request</span>
                                    <Ripple />
                                </button>)}
                            </div>
                        </div>

                        <div className="detail_container_section mt-3">
                            <div className="row detail_container_row">
                                {dutyHolderData && dutyHolderData?.length > 0 ? (
                                    <>
                                        {dutyHolderTypes && dutyHolderTypes.map((dutyHolderType) => {
                                            const dutyHolder = dutyHolderData.find((dH) => dH.dutyholder_type_id === dutyHolderType.id) || null;
                                            const isClient = dutyHolderType?.is_client === 1;
                                            const isAdded = dutyHolderType.is_added;
                                            const displayName = isClient ? dutyHolder?.client_name : dutyHolder?.name;
                                            const ableToAcceptDeclaration = dutyHolder?.this_user_part;
                                            const isDeclrationAccepted = dutyHolder?.sign_report === 1;

                                            return (
                                                <div key={`dutyHolder_${dutyHolderType.id}`} className="col-12 col-lg-6 detail_container_column">
                                                    <article className="detail_container_area dh">
                                                        <div className="detail_container_header dh">
                                                            <h6>{dutyHolderType?.dutyholder_type ?? ''}</h6>

                                                            {!isClient && !isClientUser && isAdded && !isDeclrationAccepted && isArchitect && (
                                                                <button
                                                                    className="detail_action_btn delete labeled is_btn p-ripple"
                                                                    onClick={() => handleRemoveDutyHolder(dutyHolder)}
                                                                >
                                                                    Remove
                                                                    <Ripple />
                                                                </button>
                                                            )}
                                                        </div>

                                                        <div className="detail_container_body dh">
                                                            {dutyHolderType?.is_added ? (
                                                                <>
                                                                    {dutyHolder && dutyHolder !== null && (
                                                                        <div className="detail_container">
                                                                            <div className="detail_content_group dh">
                                                                                <h6>
                                                                                    <i className="bi bi-person"></i>
                                                                                    Name :
                                                                                </h6>
                                                                                <p>
                                                                                    {displayName ? (
                                                                                        <span>{displayName}</span>
                                                                                    ) : (
                                                                                        <span className="text_italic">N/A</span>
                                                                                    )}
                                                                                </p>
                                                                            </div>

                                                                            <div className="detail_content_group dh">
                                                                                <h6>
                                                                                    <i className="bi bi-envelope"></i>
                                                                                    Email :
                                                                                </h6>
                                                                                <p>
                                                                                    {dutyHolder?.email ? (
                                                                                        <span>{dutyHolder?.email}</span>
                                                                                    ) : (
                                                                                        <span className="text_italic">N/A</span>
                                                                                    )}
                                                                                </p>
                                                                            </div>

                                                                            <div className="detail_content_group dh">
                                                                                <h6>
                                                                                    <i className="bi bi-telephone"></i>
                                                                                    Phone :
                                                                                </h6>
                                                                                <p>
                                                                                    {dutyHolder?.telephone ? (
                                                                                        <span>{dutyHolder?.telephone}</span>
                                                                                    ) : (
                                                                                        <span className="text_italic">N/A</span>
                                                                                    )}
                                                                                </p>
                                                                            </div>

                                                                            <div className="detail_content_group dh">
                                                                                <h6>
                                                                                    <i className="bi bi-geo-alt"></i>
                                                                                    Address :
                                                                                </h6>
                                                                                <p>
                                                                                    {dutyHolder?.address ? (
                                                                                        <span>{dutyHolder?.address}</span>
                                                                                    ) : (
                                                                                        <span className="text_italic">N/A</span>
                                                                                    )}
                                                                                </p>
                                                                            </div>

                                                                            {isClient ? (
                                                                                <div className="detail_content_group dh">
                                                                                    <h6>
                                                                                        <i className="bi bi-calendar2-check"></i>
                                                                                        Work Complete Date :
                                                                                    </h6>
                                                                                    <p>
                                                                                        {dutyHolder?.work_complete_date ? (
                                                                                            <span>{dutyHolder?.work_complete_date}</span>
                                                                                        ) : (
                                                                                            <span className="text_italic">N/A</span>
                                                                                        )}
                                                                                    </p>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="detail_content_group dh">
                                                                                    <h6>
                                                                                        <i className="bi bi-calendar2-event"></i>
                                                                                        Appointment Date :
                                                                                    </h6>
                                                                                    <p>
                                                                                        {dutyHolder?.appointment_date ? (
                                                                                            <span>{dutyHolder?.appointment_date}</span>
                                                                                        ) : (
                                                                                            <span className="text_italic">N/A</span>
                                                                                        )}
                                                                                    </p>
                                                                                </div>
                                                                            )}

                                                                            {isDeclrationAccepted && (
                                                                                <div className="detail_content_group dh">
                                                                                    <div className="detail_content_group_action_area success">
                                                                                        <h6>
                                                                                            <i className="bi bi-shield-fill-check"></i>
                                                                                            Declaration accepted.
                                                                                        </h6>
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {ableToAcceptDeclaration && !isDeclrationAccepted && (
                                                                                <div className="detail_content_group dh">
                                                                                    <div className="detail_content_group_action_area">
                                                                                        <h6>
                                                                                            <i className="bi bi-shield-exclamation"></i>
                                                                                            Legal declaration required. <br />Please review and accept the declaration.
                                                                                        </h6>
                                                                                        <Button
                                                                                            label="Review Declaration"
                                                                                            onClick={() => handleShowDeclarationModal(dutyHolder)}
                                                                                            className="custom_btn primary"
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div className="empty_detail_container" style={{ minHeight: '230px' }}>
                                                                    <img src="/assets/images/no_data/no_user_data.svg" alt="No data" />
                                                                    <h6>
                                                                        <i className="bi bi-exclamation-circle me-1"></i>
                                                                        <span className="text_bold">{dutyHolderType?.dutyholder_type}</span> not added yet!
                                                                    </h6>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </article>
                                                </div>
                                            )
                                        })}
                                    </>
                                ) : (
                                    <div className="page_main_content_section">
                                        <NoDataCreated message={`No duty holder found`} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Duty holder request modal */}
            <Dialog
                visible={showDutyHolderRequestModal}
                headerClassName="custom_modal_header"
                header={dutyHolderRequestModalHeader}
                footer={dutyHolderRequestModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDutyHolderRequestModal}
                dismissableMask={false}
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2 p-sm-3">
                    <div className="row">
                        {/* Duty holder type */}
                        <div className="col-12">
                            <div className="page_form_group">
                                <label htmlFor="selectedDutyHolderType" className="custom_form_label is_required">
                                    Duty holder type
                                </label>

                                <Dropdown
                                    id="selectedDutyHolderType"
                                    value={selectedDutyHolderType}
                                    onChange={(e: DropdownChangeEvent) => setSelectedDutyHolderType(e.value)}
                                    options={dutyHolderTypeOptions}
                                    optionLabel="dutyholder_type"
                                    optionDisabled="disabled"
                                    valueTemplate={selectedDutyHolderTypeTemplate}
                                    itemTemplate={dutyHolderTypeOptionTemplate}
                                    emptyFilterMessage="No duty holder type(s) found!"
                                    placeholder="Select duty holder type"
                                    className="form_dropdown address_selection w-100"
                                    showClear
                                    panelClassName="address_dropdown_panel"
                                    loading={loadingDutyHolderTypes}
                                />

                                {dutyHolderTypeError && (
                                    <small className="form_error_msg">{dutyHolderTypeError}</small>
                                )}
                            </div>
                        </div>

                        {/* Contact */}
                        <div className="col-12">
                            <DataListModalInput
                                key={`selectedContact`}
                                inputId="selectedContact"
                                label="Contact"
                                value={selectedContact}
                                displayKey="first_name"
                                onAdd={handleAddContct}
                                required={true}
                                buttonLabel="Select"
                                placeHolder="Click to select the contact"
                                error={contactError}
                                formGroupClassName="mb-0"
                            />
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Contact list modal */}
            <Dialog
                visible={showContactListModal}
                headerClassName="custom_modal_header"
                header={contactModalHeader}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseContactModal}
                contentClassName="pt-0"
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-0 p-sm-3 pt-sm-0 pb-sm-2 w-100">
                        <div className="data_select_modal_header has_tab px-0 py-3 pt-4 mb-3">
                            <div className="contact_tabs w-100 mb-1">
                                {contactTabs?.map((tab: string, index: number) => (
                                    <TabFilter
                                        key={index}
                                        tabClassName="w-100"
                                        label={tab}
                                        onTabChange={() => setContactTypeTab(tab as ContactTab)}
                                        isActive={contactTypeTab === tab}
                                    />
                                ))}
                            </div>

                            <div className="data_search_input_area">
                                <InputText
                                    key={"contactSearchKey"}
                                    id="contactSearchKey"
                                    value={contactSearchKey}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactSearchKey(e.target.value)}
                                    className="custom_form_input data_search_input"
                                    placeholder="Search contact by name"
                                    autoComplete="off"
                                    autoFocus
                                    type={"search"}
                                />
                                {contactSearchKey === "" ? (
                                    <i className="bi bi-search"></i>
                                ) : (
                                    <i
                                        className="bi bi-x-lg data_search_input_clear"
                                        onClick={() => setContactSearchKey("")}
                                    ></i>
                                )}
                            </div>
                        </div>

                        <div className="row px-sm-3 px-1">
                            <div className="col-12">
                                {(filteredContactsData && filteredContactsData?.length > 0) ? (
                                    <div className="data_select_option_area">
                                        {filteredContactsData?.map((contact) => {
                                            const profileImage = contact?.profile_image || "";
                                            const firstName = contact?.first_name || "";
                                            const lastName = contact?.last_name || "";
                                            const roleName = contact?.role_name || "";
                                            const contactNumber = contact?.mobile || "";
                                            const fullName = firstName + " " + lastName;
                                            const profileLetter = firstName[0] + lastName[0];

                                            return (
                                                <React.Fragment>
                                                    <div
                                                        key={contact?.id}
                                                        className={`contact_select_option ${selectedContact?.id === contact?.id && "selected"} p-ripple`}
                                                        onClick={() => handleSelectContact(contact)}
                                                    >
                                                        <div className="contact_prof_avatar">
                                                            {profileImage ? (
                                                                <img src={profileImage} alt={fullName} />
                                                            ) : (
                                                                <span>{profileLetter}</span>
                                                            )}
                                                        </div>
                                                        <div className="contact_prof_info">
                                                            <h6>{fullName}</h6>
                                                            <p>
                                                                <span>{roleName}</span>
                                                                <span className="text_light">
                                                                    {(roleName && contactNumber) && ' | '}
                                                                </span>
                                                                <span>{contactNumber}</span>
                                                            </p>
                                                            <p> {contact.by} </p>
                                                        </div>
                                                        <Ripple
                                                            pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" }, }, }}
                                                        />
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="no_records_found">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        No contact(s) found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Review declaration modal */}
            <Dialog
                visible={showDeclarationModal}
                headerClassName="custom_modal_header"
                header={declarationModalHeader}
                footer={declarationModalFooter}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDeclarationModal}
                dismissableMask={false}
                closeOnEscape={false}
            >
                <div className="custom_modal_body p-2">
                    <div className="row">
                        <div className="col-12">
                            <Message
                                severity='info'
                                className="message_text w-100"
                                icon="bi bi-info-circle"
                                style={{ border: '1px solid', borderRadius: '8px' }}
                                text={`Please verify your details before accepting.`}
                            />
                        </div>

                        <div className="col-12 mt-3">
                            <h4 className="view_data_head">
                                <i className="bi bi-person me-2"></i>
                                Account information
                            </h4>

                            <article className="data_view_area">
                                <ViewDataGroup
                                    title="Role"
                                    titleWrapClassName="col-5 col-sm-4"
                                    valueWrapClassName="col-7 col-sm-8"
                                    value={selectedReviewDeclaration?.dutyholder_type?.dutyholder_type}
                                />

                                <ViewDataGroup
                                    title="Email"
                                    titleWrapClassName="col-5 col-sm-4"
                                    valueWrapClassName="col-7 col-sm-8"
                                    value={selectedReviewDeclaration?.email}
                                />
                            </article>
                        </div>

                        <div className="col-12 mt-4">
                            <h4 className="view_data_head">
                                <i className="bi bi-card-checklist me-2"></i>
                                Verify & edit information
                            </h4>

                            <article className="data_view_area custom">
                                <div className="row">
                                    <div className="col-12">
                                        <TextInput
                                            key={`name`}
                                            id="name"
                                            label={selectedReviewDeclaration?.dutyholder_type?.dutyholder_type + " name"}
                                            labelHtmlFor="name"
                                            required={false}
                                            value={name}
                                            inputType="text"
                                            placeholder={selectedReviewDeclaration?.dutyholder_type?.dutyholder_type + " name"}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                                            error={nameError}
                                            formGroupClassName="mb-3"
                                        />
                                    </div>

                                    <div className="col-12">
                                        <TextInput
                                            key={`telephone`}
                                            id="telephone"
                                            label="Contact number"
                                            labelHtmlFor="telephone"
                                            required={false}
                                            inputType="text"
                                            value={telephone}
                                            placeholder="Contact number"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTelephone(e.target.value)}
                                            error={telephoneError}
                                            formGroupClassName="mb-3"
                                        />
                                    </div>

                                    <div className="col-12">
                                        <TextInput
                                            key={`address`}
                                            id="address"
                                            label="Address"
                                            labelHtmlFor="address"
                                            required={false}
                                            inputType="text"
                                            value={address}
                                            placeholder="Address"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
                                            error={addressError}
                                            formGroupClassName="mb-3"
                                        />
                                    </div>

                                    <div className="col-12">
                                        <DatePicker
                                            key={`date`}
                                            id="date"
                                            label={selectedReviewDeclaration?.dutyholder_type?.is_client === 1 ? 'Work complete date' : 'Appointment date'}
                                            labelHtmlFor="date"
                                            required={true}
                                            value={date}
                                            placeholder="dd/mm/yyyy"
                                            onChange={setDate}
                                            error={dateError}
                                            showClear
                                            onClear={() => setDate(null)}
                                            formGroupClassName="mb-1"
                                        />
                                    </div>
                                </div>
                            </article>
                        </div>

                        <div className="col-12 mt-3">
                            <div className={`response_state_area ${isAcceptDeclaration ? 'checked' : ''} dh mb-0`}>
                                <div className="response_state">
                                    <Checkbox
                                        inputId="declare"
                                        onChange={() => setIsAcceptDeclaration(!isAcceptDeclaration)}
                                        checked={isAcceptDeclaration}
                                    />
                                    <label htmlFor="declare">{declareThat}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </React.Fragment>
    )
}

export default DutyHolderDetails;